import React, { useEffect, useRef, useState } from 'react';
import { trainingReportApi } from '../../services/trainingReportApi';
import {
  buildEvidenceOrderPayload,
  formatEvidenceSize,
  MAX_EVIDENCE_DESCRIPTION_LENGTH,
  MAX_REPORT_EVIDENCES,
  moveEvidence,
  validateEvidenceFiles,
  validateEvidenceMetadata,
} from './trainingReportEvidence';
import './TrainingReportEvidence.css';

const normalizeDate = (value) => (value ? String(value).slice(0, 10) : '');

export const TrainingReportEvidenceStep = ({
  idReporte,
  accessToken,
  report,
  setReport,
  onPrevious,
}) => {
  const inputRef = useRef(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [description, setDescription] = useState('');
  const [evidenceDate, setEvidenceDate] = useState('');
  const [metadataDrafts, setMetadataDrafts] = useState({});
  const [previewUrls, setPreviewUrls] = useState({});
  const [status, setStatus] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [busyEvidenceId, setBusyEvidenceId] = useState(null);
  const [dragging, setDragging] = useState(false);

  const evidences = report?.evidencias || [];
  const remainingSlots = Math.max(0, MAX_REPORT_EVIDENCES - evidences.length);

  useEffect(() => {
    const drafts = {};
    evidences.forEach((item) => {
      drafts[item.id_evidencia] = {
        descripcion: item.descripcion || '',
        fecha_evidencia: normalizeDate(item.fecha_evidencia),
      };
    });
    setMetadataDrafts(drafts);
  }, [report]);

  useEffect(() => {
    let active = true;
    const createdUrls = [];

    const loadPreviews = async () => {
      if (!accessToken || evidences.length === 0) {
        setPreviewUrls({});
        return;
      }

      const entries = await Promise.all(
        evidences.map(async (evidence) => {
          try {
            const blob = await trainingReportApi.evidenceFile(
              idReporte,
              evidence.id_evidencia,
              accessToken,
            );
            if (!active) return null;
            const url = URL.createObjectURL(blob);
            createdUrls.push(url);
            return [evidence.id_evidencia, url];
          } catch (error) {
            console.error('No se pudo cargar la vista previa de evidencia:', error);
            return [evidence.id_evidencia, null];
          }
        }),
      );

      if (active) {
        setPreviewUrls(
          Object.fromEntries(entries.filter(Boolean)),
        );
      }
    };

    loadPreviews();
    return () => {
      active = false;
      createdUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [accessToken, idReporte, report]);

  const addFiles = (incoming) => {
    const combined = [...selectedFiles, ...Array.from(incoming || [])];
    const validation = validateEvidenceFiles(combined, evidences.length);
    if (validation.error) {
      setStatus({ type: 'error', message: validation.error });
      return;
    }
    setSelectedFiles(validation.files);
    setStatus(null);
  };

  useEffect(() => {
    const handlePaste = (event) => {
      const imageFiles = Array.from(event.clipboardData?.files || []).filter(
        (file) => String(file.type || '').startsWith('image/'),
      );
      if (imageFiles.length === 0) return;
      event.preventDefault();
      addFiles(imageFiles);
      setStatus({
        type: 'info',
        message: `${imageFiles.length} imagen(es) pegada(s) desde el portapapeles.`,
      });
    };

    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  });

  const removeSelectedFile = (index) => {
    setSelectedFiles((current) => current.filter((_file, itemIndex) => itemIndex !== index));
    setStatus(null);
  };

  const uploadFiles = async () => {
    if (!accessToken || uploading || selectedFiles.length === 0) return;

    const fileValidation = validateEvidenceFiles(selectedFiles, evidences.length);
    if (fileValidation.error) {
      setStatus({ type: 'error', message: fileValidation.error });
      return;
    }

    const metadataErrors = validateEvidenceMetadata({
      descripcion: description,
      fecha_evidencia: evidenceDate,
    });
    if (Object.keys(metadataErrors).length > 0) {
      setStatus({
        type: 'error',
        message: metadataErrors.descripcion || metadataErrors.fecha_evidencia,
      });
      return;
    }

    setUploading(true);
    setStatus({ type: 'info', message: 'Subiendo evidencias...' });
    try {
      const updated = await trainingReportApi.uploadEvidence(
        idReporte,
        fileValidation.files,
        {
          descripcion: description.trim() || null,
          fecha_evidencia: evidenceDate || null,
        },
        accessToken,
      );
      setReport(updated);
      setSelectedFiles([]);
      setDescription('');
      setEvidenceDate('');
      if (inputRef.current) inputRef.current.value = '';
      setStatus({
        type: 'success',
        message: `${fileValidation.files.length} evidencia(s) subida(s) correctamente.`,
      });
    } catch (error) {
      console.error('No se pudieron subir las evidencias:', error);
      setStatus({
        type: 'error',
        message: error?.message || 'No fue posible subir las evidencias.',
      });
    } finally {
      setUploading(false);
    }
  };

  const updateDraft = (idEvidence, field, value) => {
    setMetadataDrafts((current) => ({
      ...current,
      [idEvidence]: {
        ...(current[idEvidence] || {}),
        [field]: value,
      },
    }));
    setStatus(null);
  };

  const saveEvidenceMetadata = async (evidence) => {
    const draft = metadataDrafts[evidence.id_evidencia] || {};
    const validation = validateEvidenceMetadata(draft);
    if (Object.keys(validation).length > 0) {
      setStatus({
        type: 'error',
        message: validation.descripcion || validation.fecha_evidencia,
      });
      return;
    }

    setBusyEvidenceId(evidence.id_evidencia);
    setStatus({ type: 'info', message: 'Guardando datos de la evidencia...' });
    try {
      const updated = await trainingReportApi.updateEvidence(
        idReporte,
        evidence.id_evidencia,
        {
          descripcion: String(draft.descripcion || '').trim() || null,
          fecha_evidencia: draft.fecha_evidencia || null,
        },
        accessToken,
      );
      setReport(updated);
      setStatus({ type: 'success', message: 'Evidencia actualizada.' });
    } catch (error) {
      console.error('No se pudo actualizar la evidencia:', error);
      setStatus({
        type: 'error',
        message: error?.message || 'No fue posible actualizar la evidencia.',
      });
    } finally {
      setBusyEvidenceId(null);
    }
  };

  const deleteEvidence = async (evidence) => {
    const confirmed = window.confirm(
      `¿Eliminar la evidencia “${evidence.nombre_original || evidence.nombre_archivo || evidence.id_evidencia}”?`,
    );
    if (!confirmed) return;

    setBusyEvidenceId(evidence.id_evidencia);
    setStatus({ type: 'info', message: 'Eliminando evidencia...' });
    try {
      const updated = await trainingReportApi.deleteEvidence(
        idReporte,
        evidence.id_evidencia,
        accessToken,
      );
      setReport(updated);
      setStatus({ type: 'success', message: 'Evidencia eliminada.' });
    } catch (error) {
      console.error('No se pudo eliminar la evidencia:', error);
      setStatus({
        type: 'error',
        message: error?.message || 'No fue posible eliminar la evidencia.',
      });
    } finally {
      setBusyEvidenceId(null);
    }
  };

  const reorder = async (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= evidences.length || busyEvidenceId !== null) return;
    const reordered = moveEvidence(evidences, fromIndex, toIndex);
    const moving = evidences[fromIndex];
    setBusyEvidenceId(moving?.id_evidencia || -1);
    setStatus({ type: 'info', message: 'Actualizando orden de evidencias...' });
    try {
      const updated = await trainingReportApi.reorderEvidence(
        idReporte,
        buildEvidenceOrderPayload(reordered),
        accessToken,
      );
      setReport(updated);
      setStatus({ type: 'success', message: 'Orden actualizado.' });
    } catch (error) {
      console.error('No se pudo reordenar la evidencia:', error);
      setStatus({
        type: 'error',
        message: error?.message || 'No fue posible reordenar las evidencias.',
      });
    } finally {
      setBusyEvidenceId(null);
    }
  };

  const statusClass =
    status?.type === 'success'
      ? 'admin-operation-status-success'
      : status?.type === 'error'
        ? 'admin-operation-status-error'
        : 'admin-operation-status-warning';

  return (
    <>
      <div className="admin-operation-panel-heading">
        <div>
          <p className="admin-operation-eyebrow">Paso 4 de 7</p>
          <h2>Evidencias</h2>
          <p>
            Adjunta las capturas o fotografías que formarán el Anexo 1. Puedes
            seleccionar archivos, arrastrarlos aquí o pegar imágenes con Ctrl+V.
          </p>
        </div>
        <span className={`training-report-status training-report-status-${report?.estado || 'borrador'}`}>
          {report?.estado === 'generado' ? 'Generado' : 'Borrador'}
        </span>
      </div>

      <div className="training-report-evidence-counter">
        <span>Evidencias</span>
        <strong>{evidences.length} / {MAX_REPORT_EVIDENCES}</strong>
        <small>{remainingSlots} espacio(s) disponible(s)</small>
      </div>

      <div
        className={`training-report-evidence-dropzone ${dragging ? 'training-report-evidence-dropzone-active' : ''}`}
        onDragEnter={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={(event) => {
          event.preventDefault();
          if (event.currentTarget === event.target) setDragging(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          addFiles(event.dataTransfer.files);
        }}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') inputRef.current?.click();
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,.jpg,.jpeg,.png"
          multiple
          hidden
          onChange={(event) => addFiles(event.target.files)}
          disabled={uploading || remainingSlots === 0}
        />
        <strong>Arrastra imágenes aquí o haz clic para seleccionarlas</strong>
        <span>También puedes copiar una imagen y pegarla con Ctrl+V</span>
        <small>JPG, JPEG o PNG · máximo 5 MB por imagen · máximo 20 evidencias</small>
      </div>

      {selectedFiles.length > 0 && (
        <div className="training-report-evidence-upload-panel">
          <div className="training-report-selected-files">
            <strong>{selectedFiles.length} archivo(s) por subir</strong>
            {selectedFiles.map((file, index) => (
              <div key={`${file.name}-${index}`}>
                <span>{file.name} · {formatEvidenceSize(file.size)}</span>
                <button
                  type="button"
                  onClick={() => removeSelectedFile(index)}
                  disabled={uploading}
                >
                  Quitar
                </button>
              </div>
            ))}
          </div>

          <div className="training-report-evidence-upload-fields">
            <div className="admin-operation-field">
              <label htmlFor="training-report-evidence-date">Fecha de evidencia</label>
              <input
                id="training-report-evidence-date"
                type="date"
                value={evidenceDate}
                onChange={(event) => setEvidenceDate(event.target.value)}
                disabled={uploading}
              />
            </div>
            <div className="admin-operation-field training-report-evidence-description-field">
              <label htmlFor="training-report-evidence-description">Descripción</label>
              <input
                id="training-report-evidence-description"
                value={description}
                maxLength={MAX_EVIDENCE_DESCRIPTION_LENGTH}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="Ej. Evidencia de la sesión del 27 de julio"
                disabled={uploading}
              />
              <small>{description.length} / {MAX_EVIDENCE_DESCRIPTION_LENGTH}</small>
            </div>
            <button
              className="admin-operation-button"
              type="button"
              onClick={uploadFiles}
              disabled={uploading}
            >
              {uploading ? 'Subiendo...' : 'Subir evidencias'}
            </button>
          </div>
        </div>
      )}

      {status && (
        <div className={`admin-operation-status ${statusClass}`} role="status">
          {status.message}
        </div>
      )}

      {evidences.length === 0 ? (
        <p className="admin-operation-empty training-report-evidence-empty">
          Todavía no hay evidencias. El PDF definitivo requerirá al menos una.
        </p>
      ) : (
        <div className="training-report-evidence-grid">
          {evidences.map((evidence, index) => {
            const draft = metadataDrafts[evidence.id_evidencia] || {};
            const isBusy = busyEvidenceId === evidence.id_evidencia;
            return (
              <article className="training-report-evidence-card" key={evidence.id_evidencia}>
                <div className="training-report-evidence-preview">
                  {previewUrls[evidence.id_evidencia] ? (
                    <img
                      src={previewUrls[evidence.id_evidencia]}
                      alt={evidence.descripcion || evidence.nombre_original || `Evidencia ${index + 1}`}
                    />
                  ) : (
                    <span>Vista previa no disponible</span>
                  )}
                  <strong>{index + 1}</strong>
                </div>

                <div className="training-report-evidence-card-body">
                  <div className="training-report-evidence-file-info">
                    <strong>{evidence.nombre_original || evidence.nombre_archivo}</strong>
                    <span>{formatEvidenceSize(evidence.tamano_bytes)}</span>
                  </div>

                  <div className="admin-operation-field">
                    <label htmlFor={`evidence-date-${evidence.id_evidencia}`}>Fecha</label>
                    <input
                      id={`evidence-date-${evidence.id_evidencia}`}
                      type="date"
                      value={draft.fecha_evidencia || ''}
                      onChange={(event) => updateDraft(evidence.id_evidencia, 'fecha_evidencia', event.target.value)}
                      disabled={isBusy}
                    />
                  </div>

                  <div className="admin-operation-field">
                    <label htmlFor={`evidence-description-${evidence.id_evidencia}`}>Descripción</label>
                    <textarea
                      id={`evidence-description-${evidence.id_evidencia}`}
                      value={draft.descripcion || ''}
                      maxLength={MAX_EVIDENCE_DESCRIPTION_LENGTH}
                      onChange={(event) => updateDraft(evidence.id_evidencia, 'descripcion', event.target.value)}
                      placeholder="Descripción opcional"
                      disabled={isBusy}
                    />
                  </div>

                  <div className="training-report-evidence-card-actions">
                    <button
                      className="admin-operation-button admin-operation-button-secondary admin-operation-button-compact"
                      type="button"
                      onClick={() => reorder(index, index - 1)}
                      disabled={index === 0 || busyEvidenceId !== null}
                    >
                      Subir
                    </button>
                    <button
                      className="admin-operation-button admin-operation-button-secondary admin-operation-button-compact"
                      type="button"
                      onClick={() => reorder(index, index + 1)}
                      disabled={index === evidences.length - 1 || busyEvidenceId !== null}
                    >
                      Bajar
                    </button>
                    <button
                      className="admin-operation-button admin-operation-button-compact"
                      type="button"
                      onClick={() => saveEvidenceMetadata(evidence)}
                      disabled={busyEvidenceId !== null}
                    >
                      Guardar datos
                    </button>
                    <button
                      className="admin-operation-button admin-operation-button-danger admin-operation-button-compact"
                      type="button"
                      onClick={() => deleteEvidence(evidence)}
                      disabled={busyEvidenceId !== null}
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <div className="training-report-editor-actions">
        <button
          className="admin-operation-button admin-operation-button-secondary"
          type="button"
          onClick={onPrevious}
          disabled={uploading || busyEvidenceId !== null}
        >
          Volver a resultados
        </button>
      </div>
    </>
  );
};
